/**
 * /tenant-analytics — Per-tenant A/B funnel for the white-label landing.
 *
 * Tenants on the Premium plan land here from the sidebar. Reads
 * /api/analytics/tenant-stats which is automatically scoped to the JWT's
 * tenant_id, so each tenant sees ONLY their own /p/:slug events.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, TrendingUp, Trophy, Eye, MousePointerClick } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PALETTE = ["#C9A24E", "#10B981", "#3B82F6", "#EC4899"];

export default function TenantAnalytics() {
  const [windowDays, setWindowDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true); setError("");
    axios.get(`${API}/analytics/tenant-stats?days=${windowDays}`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [windowDays]);

  const variants = useMemo(() => {
    if (!data?.variants) return [];
    return Object.entries(data.variants).map(([k, v]) => ({ key: k, ...v }))
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [data]);
  const totals = useMemo(() => {
    const i = variants.reduce((s, v) => s + (v.impression || 0), 0);
    const c = variants.reduce((s, v) => s + (v.click || 0), 0);
    return { impr: i, clk: c, ctr: i ? Math.round((c / i) * 10000) / 100 : 0 };
  }, [variants]);
  const maxCtr = Math.max(0.01, ...variants.map((v) => v.ctr || 0));

  return (
    <div data-testid="tenant-analytics-page" className="min-h-screen p-6 lg:p-8" style={{ background: "#0F0820", color: "#FFFFFF" }}>
      <div className="max-w-5xl mx-auto space-y-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 data-testid="tenant-analytics-h1" className="font-display text-3xl font-black">Your landing analytics</h1>
            <p className="text-[13.5px] mt-1 text-white/60">
              {data?.tenant_slug ? <>White-label landing: <code className="font-mono text-[#C9A24E]">/p/{data.tenant_slug}</code></> : "—"}
            </p>
          </div>
          <div className="inline-flex rounded-md p-0.5 border" data-testid="tenant-analytics-window" style={{ borderColor: "rgba(201,162,78,0.25)" }}>
            {[7, 30, 90].map((d) => {
              const active = windowDays === d;
              return (
                <button key={d} type="button" onClick={() => setWindowDays(d)}
                  data-testid={`tenant-analytics-window-${d}`}
                  className="px-3 h-8 text-[11.5px] font-bold tracking-wider rounded transition-colors"
                  style={{ backgroundColor: active ? "#C9A24E" : "transparent", color: active ? "#0F0820" : "rgba(255,255,255,0.7)" }}>{d}d</button>
              );
            })}
          </div>
        </div>

        {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#C9A24E]" /></div>
         : error ? <div data-testid="tenant-analytics-error" className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-red-300 text-[13px]">{error}</div>
         : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="tenant-analytics-kpis">
              <Kpi icon={Eye} label="Impressions" value={fmt(totals.impr)} />
              <Kpi icon={MousePointerClick} label="Clicks" value={fmt(totals.clk)} color="#10B981" />
              <Kpi icon={TrendingUp} label="Blended CTR" value={`${totals.ctr.toFixed(2)}%`} color="#3B82F6" />
              <Kpi icon={Trophy} label="Winning variant" value={data?.winner || "—"} color="#C9A24E"
                badge={data?.winner ? (data.winner_floor_met ? "Significant" : "Tentative") : null} />
            </div>
            <div className="rounded-xl border p-6" style={{ backgroundColor: "#1A0E36", borderColor: "rgba(201,162,78,0.18)" }}>
              <div className="text-[10.5px] font-mono uppercase tracking-[0.18em] text-white/55 mb-5">Variant performance</div>
              {variants.length ? variants.map((v, i) => (
                <div key={v.key} data-testid={`tenant-variant-${v.key}`} className="mb-4">
                  <div className="flex items-center justify-between text-[12.5px] mb-1.5">
                    <div className="flex items-center gap-2 font-bold text-white">
                      <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PALETTE[i % PALETTE.length] }} />
                      Variant {v.key}
                      {v.key === data?.winner && <Trophy className="w-3.5 h-3.5" style={{ color: "#C9A24E" }} />}
                    </div>
                    <div className="text-white/65 tabular-nums">
                      <span className="text-white font-bold">{v.ctr.toFixed(2)}%</span> CTR · {fmt(v.impression || 0)} impr · {fmt(v.click || 0)} clicks
                    </div>
                  </div>
                  <div className="h-2.5 rounded-full overflow-hidden" style={{ backgroundColor: "rgba(255,255,255,0.06)" }}>
                    <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(2, (v.ctr / maxCtr) * 100)}%`, backgroundColor: PALETTE[i % PALETTE.length] }} />
                  </div>
                </div>
              )) : (
                <div data-testid="tenant-analytics-empty" className="text-center text-white/55 py-8">No traffic yet on your white-label landing. Share <code className="font-mono text-[#C9A24E]">/p/{data?.tenant_slug}</code> to start collecting data.</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, color = "#C9A24E", badge }) {
  return (
    <div className="rounded-xl border p-4 flex flex-col gap-1.5"
      style={{ backgroundColor: "#1A0E36", borderColor: "rgba(201,162,78,0.18)" }}>
      <div className="flex items-center gap-2 text-[10.5px] font-mono uppercase tracking-[0.18em] text-white/55">
        <Icon className="w-3.5 h-3.5" style={{ color }} /> {label}
      </div>
      <div className="flex items-center gap-2">
        <div className="font-display font-black text-3xl tabular-nums" style={{ color: "#FFFFFF" }}>{value}</div>
        {badge && (<span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded" style={{ backgroundColor: badge === "Significant" ? "#10B981" : "#71717A", color: "#0F0820" }}>{badge}</span>)}
      </div>
    </div>
  );
}

function fmt(n) { return new Intl.NumberFormat("en-US").format(n || 0); }
